/**
 * Planning module — weekly board (orchestrator).
 *
 * The page itself is now thin: state for filters/dialogs, drag glue, and
 * layout. All real logic lives in /pages/planning/:
 *   - hooks/usePlanningBoard.js       : data + memoised indices
 *   - hooks/useAssignmentMutations.js : create / batch / delete / copy-week
 *   - EmployeeTile.jsx, FirmGroup.jsx, FilterPopover.jsx, FilterPill.jsx,
 *     QuickAssignDialog.jsx           : presentation
 *   - utils.js                        : ISO week math + static styles
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, ChevronsRight, ChevronsDown, ChevronsUp, Calendar as CalendarIcon,
  Loader2, Filter, Copy, AlertCircle, AlertTriangle, CheckCircle2, Building2, Lock,
} from 'lucide-react';

import { DAY_NAMES, weekDates as toWeekDates, getISOWeekNow, normalizeTr, columnHeaderLabel } from './planning/utils';
import { useTranslation } from '../lib/i18n';
import { FirmGroup } from './planning/FirmGroup';
import { useRitmonitorProgress } from './planning/useRitmonitorProgress';
import { RideDetailDrawer } from './planning/RideDetailDrawer';
// Toolbar primitives (FilterPopover, FilterPill, WeekPicker, FirmCountChips,
// PlanningActionsMenu) live inside <PlanningToolbar/> — they are imported there.
import { ActiveFiltersBreadcrumb } from './planning/ActiveFiltersBreadcrumb';
import { PlanningLeftPanel } from './planning/PlanningLeftPanel';
import { PlanningToolbar } from './planning/PlanningToolbar';
import { QuickAssignDialog } from './planning/QuickAssignDialog';
import { ConfirmDialog } from './planning/ConfirmDialog';
import { CellContextMenu } from './planning/CellContextMenu';
import { DepotManagerDialog } from './planning/DepotManagerDialog';
import { ConflictsDialog } from './planning/ConflictsDialog';
import { CopyConflictsSection } from './planning/CopyConflictsSection';
import { ImportExportDialog } from './planning/ImportExportDialog';
import { SubcontractorDialog } from './planning/SubcontractorDialog';
import { usePlanningBoard } from './planning/usePlanningBoard';
import { useAssignmentMutations } from './planning/useAssignmentMutations';

export default function Planning() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  // Atama / düzenleme yetkisi: SUPER_ADMIN her zaman + sayfa yetkilerinde
  // 'planning-edit' işaretli FULL_ADMIN/FIRM_ADMIN. Eski davranışla geriye
  // uyumlu kalmak için: bu anahtara sahip olmayan ama 'planning' yetkisine
  // sahip eski kullanıcılar SADECE okuma yapabilir (atama butonları, drag,
  // sağ-tık menüsü vs. gizlenir).
  const canEditPlanning = (() => {
    if (isSuperAdmin) return true;
    const pages = user?.permissions?.allowed_pages || [];
    return pages.includes('planning-edit');
  })();
  // Şub 2026 — "Önceki Haftadan Kopyala" ayrı yetki
  const canCopyWeek = (() => {
    if (isSuperAdmin) return true;
    if (!['FULL_ADMIN', 'FIRM_ADMIN'].includes(user?.role)) return false;
    const pages = user?.permissions?.allowed_pages || [];
    // allowed_pages boşsa geri-uyumluluk: eski FULL_ADMIN'lere serbest
    if (!pages || pages.length === 0) return true;
    return pages.includes('planning-copy');
  })();

  // -------- Week navigation --------
  // Honour ?year & ?week query params on first mount so the deep-links from
  // the Listele / Özet pages land on the correct board.
  const _initial = useMemo(() => {
    const now = getISOWeekNow();
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const y = parseInt(params.get('year'), 10);
      const w = parseInt(params.get('week'), 10);
      if (Number.isFinite(y) && Number.isFinite(w) && w >= 1 && w <= 53) {
        return { year: y, week: w };
      }
    }
    return now;
  }, []);
  const [year, setYear] = useState(_initial.year);
  const [week, setWeek] = useState(_initial.week);
  const dates = useMemo(() => toWeekDates(year, week), [year, week]);

  // -------- Filters --------
  // Two INDEPENDENT firm filters:
  //  - empFirmIds       : Left panel — only filters which employees are listed (multi-select).
  //  - filterFirmIds    : Top toolbar — board context (owners shown, drop firm
  //                       attribution, "Ödünç" detection on cells, default
  //                       Depo Yönetimi scope) — multi-select.
  // Keeping them separate so the two panels stop "fighting" each other.
  const [search, setSearch] = useState('');
  const [filterCompanyIds, setFilterCompanyIds] = useState(() => new Set());
  const [filterDepotIds, setFilterDepotIds] = useState(() => new Set());
  const [filterRouteIds, setFilterRouteIds] = useState(() => new Set());
  const [filterFirmIds, setFilterFirmIds] = useState(() => new Set()); // board / right side multi-select
  const [empFirmIds, setEmpFirmIds] = useState(new Set()); // left panel multi-select
  const [includeBorrowed, setIncludeBorrowed] = useState(false);
  const [empFilter, setEmpFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // When exactly one board firm is selected, several single-firm flows
  // (locked-date fetch, "Add Depo" scope, default firm_id on assign) reuse it.
  // With 0 or 2+ selected, those flows fall back to "global / pick on dialog".
  const singleBoardFirmId = filterFirmIds.size === 1 ? [...filterFirmIds][0] : '';

  // -------- Dialog state --------
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  // Şub 2026 — Kopyalama ön izleme (dry-run) state'i
  const [copyPreview, setCopyPreview] = useState(null); // { copied, skipped, conflicts, would_overwrite, target_existing, __overwrite }
  const [copyPreviewLoading, setCopyPreviewLoading] = useState(false);
  const [quickAssign, setQuickAssign] = useState(null); // {emp, date} | null
  const [draggedEmployee, setDraggedEmployee] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // {title, message, onConfirm, destructive?}
  const [ctxMenu, setCtxMenu] = useState(null);          // {x, y, assignment, route}
  const [depotManagerOpen, setDepotManagerOpen] = useState(false);
  const [depotManagerScopeCompany, setDepotManagerScopeCompany] = useState(null);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [importExportOpen, setImportExportOpen] = useState(false);

  // Focus modu — operatör soldaki çalışan kartına çift tıklarsa o kişinin
  // tahtadaki tüm atamaları kırmızı ring + glow ile öne çıkar. Aynı kişiye
  // tekrar çift tıkla veya boşluğa tıklayarak modu kapatır. Drag-and-drop
  // / context-menu / row scroll davranışlarını ETKİLEMEZ — sadece görsel.
  // ESC tuşu modu kapatır (klavyeden hızlı çıkış).
  const [focusedEmpId, setFocusedEmpId] = useState(null);
  const toggleFocusedEmp = (empId) => setFocusedEmpId((cur) => (cur === empId ? null : empId));
  useEffect(() => {
    if (!focusedEmpId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFocusedEmpId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusedEmpId]);

  // -------- Column resizer (Firma / Depo / Rota) --------
  // Persisted in localStorage so the operator's preferred sticky-column
  // width survives reloads. Bounds [120, 360] keep the layout sane.
  const [firstColWidth, setFirstColWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('planning.firstColWidth') || '170', 10);
      return Number.isFinite(v) && v >= 120 && v <= 360 ? v : 170;
    } catch { return 170; }
  });
  const onFirstColResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = firstColWidth;
    const onMove = (ev) => {
      const next = Math.max(120, Math.min(360, startW + (ev.clientX - startX)));
      setFirstColWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem('planning.firstColWidth', String(firstColWidthRef.current)); } catch { /* ignore */ }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  // Mirror state into a ref so the up-handler reads the latest width
  // without re-binding listeners on every move.
  const firstColWidthRef = useRef(firstColWidth);
  useEffect(() => { firstColWidthRef.current = firstColWidth; }, [firstColWidth]);

  // -------- Locked dates (resmi tatiller / Çalışma Durumu V2 kilitleri) --------
  // Pull the union of locked dates that fall in the visible week. Backend
  // honors firm scope: when a firm filter is active, that firm's locks +
  // global locks; with no firm filter only global (1 Mayıs vb.) come back.
  // Fetched ABOVE usePlanningBoard so that hook can subtract locked weekdays
  // from the "boş hücre" total — locked days aren't actually missing.
  const [lockedDates, setLockedDates] = useState({}); // {YYYY-MM-DD: reason}
  useEffect(() => {
    if (!dates?.length) { setLockedDates({}); return; }
    const start = dates[0];
    const end = dates[dates.length - 1];
    const params = new URLSearchParams({ start_date: start, end_date: end });
    if (singleBoardFirmId) params.set('firm_id', singleBoardFirmId);
    else if (isSuperAdmin) params.set('firm_id', ''); // global-only
    api.get(`/admin/locked-dates/for-user?${params.toString()}`)
      .then((res) => {
        const map = {};
        for (const item of (res.data?.data || [])) {
          if (item?.date) map[item.date] = item.reason || 'Kilitli tarih';
        }
        setLockedDates(map);
      })
      .catch(() => setLockedDates({}));
  }, [dates, singleBoardFirmId, isSuperAdmin]);
  const isDateLocked = (d) => Boolean(lockedDates && lockedDates[d]);

  // Madde 3 (Mayıs 2026 güncelleme): Cumartesi ARTIK varsayılan olarak
  // tabloda görünür — operatör isteği "cumartesi hep çalışma var". Toggle
  // "İşlemler" menüsünden kaldırıldı. Pazar default GİZLİ kalmaya devam
  // ediyor; toolbar toggle'ı sadece o gün için açar. Resmi tatil/kilitli
  // günlerde column yine de gizlenir (banner'da yer aldığı için).
  const [showSaturday] = useState(true);
  const [showSunday, setShowSunday] = useState(false);
  const isDayHidden = (i, dateStr) => {
    if (i < 5) return false;
    const lockedToday = isDateLocked(dateStr);
    if (i === 5) return !showSaturday || lockedToday;
    if (i === 6) return !showSunday;
    return false;
  };
  const visibleDates = useMemo(() => {
    if (!dates?.length) return dates;
    return dates.filter((d, i) => !isDayHidden(i, d));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, showSaturday, showSunday, lockedDates]);

  // -------- PostNL Ritmonitor live progress --------
  // Bugün'ün ISO tarihi (board görünen aralık içeriyorsa hook'u aktif et).
  const todayISO = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);
  const ritmonitorEnabled = useMemo(
    () => Array.isArray(visibleDates) && visibleDates.includes(todayISO),
    [visibleDates, todayISO],
  );
  const ritmonitor = useRitmonitorProgress({ enabled: ritmonitorEnabled });
  // Drawer state — chip'e tıklayınca açılır
  const [rideDrawer, setRideDrawer] = useState(null); // {ritnaam, liveSnapshot}
  const openRideDrawer = useCallback((ritnaam, liveSnapshot) => {
    setRideDrawer({ ritnaam, liveSnapshot });
  }, []);

  // -------- Data + derived indices --------
  const {
    board, employees, loading,
    routeGroups, assignByCell, leaveByEmpDate,
    employeeBookedDates, employeeDoubleBookedDates, employeeWeekSummary,
    conflictList,
    missingCount, conflictCount,
    assignedCount, sundayAssignedCount,
    fetchBoard,
  } = usePlanningBoard({ year, week, filterFirmIds, filterCompanyIds, filterDepotIds, filterRouteIds, lockedDates, showSaturday });

  // -------- Mutations --------
  const {
    createAssignmentFor, createAssignmentForWeek,
    removeAssignment, moveAssignment, copyFromPreviousWeek, copyDayToDay, spreadDayToRestOfWeek,
  } = useAssignmentMutations({
    year, week, filterFirmId: singleBoardFirmId,
    leaveByEmpDate, assignByCell, employeeBookedDates, fetchBoard,
    lockedDates,
  });

  // "Sonraki güne kopyala" — for column header arrow buttons. Picks the
  // un-filtered assignments from the source day (so we copy the real plan,
  // not just what the firm filter is currently showing). Cumartesi/Pazar
  // sütunları kapalı ya da kilitliyse onları atlayıp sonraki görünür güne
  // kopyalarız — operatör görmediği güne kopyalama beklemez.
  const handleCopyDayToNext = (srcIdx) => {
    if (!board || srcIdx >= 6) return;
    const srcDate = dates[srcIdx];
    // Sonraki görünür gün (Pazar dışı) bul.
    let tgtIdx = srcIdx + 1;
    while (tgtIdx <= 5 && isDayHidden(tgtIdx, dates[tgtIdx])) tgtIdx += 1;
    if (tgtIdx > 5) {
      toast.info('Kopyalanacak görünür gün yok');
      return;
    }
    const tgtDate = dates[tgtIdx];
    const srcAssignments = (board.assignments || []).filter((a) => a.assignment_date === srcDate);
    if (srcAssignments.length === 0) {
      toast.info('Bu günde atama yok');
      return;
    }
    setConfirmState({
      title: `${DAY_NAMES[srcIdx]} → ${DAY_NAMES[tgtIdx]}`,
      message: `${srcAssignments.length} atama ${tgtDate} gününe kopyalanacak.\nÇakışan kayıtlar atlanır.`,
      confirmLabel: 'Kopyala',
      onConfirm: () => {
        copyDayToDay(srcDate, tgtDate, srcAssignments);
        setConfirmState(null);
      },
    });
  };

  // "Tüm haftaya yay" — spread the source day to every later WEEKDAY in the
  // week (Pazar hariç — operatör manuel atayacak).
  const handleSpreadDay = (srcIdx) => {
    if (!board || srcIdx >= 6) return;
    const srcDate = dates[srcIdx];
    // Targets = sadece görünür Pzt-Cmt günleri. Cumartesi/Pazar UI'da
    // kapatılmış (toggle off) ya da kilitli ise toplu yayma onları atlar —
    // operatörün niyetiyle çelişmesin.
    const targetDates = visibleDates.filter((d, i) => {
      const realIdx = dates.indexOf(d);
      return realIdx > srcIdx && realIdx <= 5; // Pazar (6) zaten hariç
    });
    if (targetDates.length === 0) {
      toast.info('Yayılacak görünür gün yok');
      return;
    }
    const srcAssignments = (board.assignments || []).filter((a) => a.assignment_date === srcDate);
    if (srcAssignments.length === 0) {
      toast.info('Bu günde atama yok');
      return;
    }
    setConfirmState({
      title: `${DAY_NAMES[srcIdx]} gününü kalan haftaya yay`,
      message: `${srcAssignments.length} atama, kalan ${targetDates.length} göründürülen güne yayılacak.\nKapatılmış (Cmt/Paz) ve kilitli günler atlanır.\nÇakışan kayıtlar atlanır.`,
      confirmLabel: 'Yay',
      onConfirm: () => {
        // Hook'a doğrudan hedef tarihleri ver (allDates yerine targetDates).
        spreadDayToRestOfWeek(srcDate, dates, srcAssignments, targetDates);
        setConfirmState(null);
      },
    });
  };

  // Right-click action: spread a SINGLE (employee, route) pair from this day
  // to every later WEEKDAY (Pazar hariç) in the week.
  const handleSpreadEmployeeFromCell = (assignment) => {
    if (!assignment) return;
    const idx = dates.indexOf(assignment.assignment_date);
    if (idx < 0 || idx >= 6) return;
    const targetDates = visibleDates.filter((d) => {
      const realIdx = dates.indexOf(d);
      return realIdx > idx && realIdx <= 5;
    });
    if (targetDates.length === 0) {
      toast.info('Yayılacak görünür gün yok');
      return;
    }
    setConfirmState({
      title: `${assignment.employee_name} kalan haftaya yay`,
      message: `${assignment.route_code} rotası, ${assignment.employee_name} ile ${targetDates.length} göründürülen güne yayılacak.\nKapatılmış (Cmt/Paz) ve kilitli günler atlanır.\nÇakışan kayıtlar atlanır.`,
      confirmLabel: 'Yay',
      onConfirm: () => {
        spreadDayToRestOfWeek(assignment.assignment_date, dates, [assignment], targetDates);
        setConfirmState(null);
      },
    });
  };

  // Right-click action: copy a SINGLE assignment to the next day. Cmt/Paz
  // kapalı ya da kilitli ise sonraki görünür güne atlanır.
  const handleCopyCellToNext = (assignment) => {
    if (!assignment) return;
    const idx = dates.indexOf(assignment.assignment_date);
    if (idx < 0 || idx >= 6) return;
    let tgtIdx = idx + 1;
    while (tgtIdx <= 5 && isDayHidden(tgtIdx, dates[tgtIdx])) tgtIdx += 1;
    if (tgtIdx > 5) {
      toast.info('Kopyalanacak görünür gün yok');
      return;
    }
    const tgtDate = dates[tgtIdx];
    setConfirmState({
      title: `Sonraki güne kopyala`,
      message: `${assignment.employee_name} (${assignment.route_code}) ${tgtDate} gününe kopyalanacak.`,
      confirmLabel: 'Kopyala',
      onConfirm: () => {
        copyDayToDay(assignment.assignment_date, tgtDate, [assignment]);
        setConfirmState(null);
      },
    });
  };

  // Right-click action: confirm + remove a single assignment.
  const handleRemoveFromCtx = (assignment) => {
    if (!assignment) return;
    setConfirmState({
      title: 'Atamayı sil',
      message: `${assignment.employee_name} · ${assignment.route_code} (${assignment.assignment_date}) silinecek.`,
      confirmLabel: 'Sil',
      destructive: true,
      onConfirm: () => {
        removeAssignment(assignment);
        setConfirmState(null);
      },
    });
  };

  // Şub 2026 — Aktif toolbar filtrelerini backend copy-week payload'ına çevirir.
  // Kaynak haftadan yalnızca operatörün UI'da görünür kıldığı kapsamdaki
  // atamalar kopyalanır (örn. firma X ve depo Y filtresi aktifse, o firmanın
  // o depodaki rotaları). empFilter (izinli/atanmış) çalışan bazlı olduğu için
  // burada dahil edilmez — atama görselinin kendisi filtreleme kriteri değil.
  const activeCopyFilters = useMemo(() => {
    const f = {};
    if (filterFirmIds.size > 0) f.firm_ids = Array.from(filterFirmIds);
    if (filterCompanyIds.size > 0) f.company_ids = Array.from(filterCompanyIds);
    if (filterDepotIds.size > 0) f.depot_ids = Array.from(filterDepotIds);
    if (filterRouteIds.size > 0) f.route_ids = Array.from(filterRouteIds);
    return f;
  }, [filterFirmIds, filterCompanyIds, filterDepotIds, filterRouteIds]);
  const hasActiveCopyFilters = Object.keys(activeCopyFilters).length > 0;

  // Şub 2026 — Kopyalama ön izlemesi (dry-run): DB'ye yazmadan farkları hesaplar.
  const copyPreviewFromPreviousWeek = async (overwrite, includeDouble = false) => {
    let srcWeek = week - 1; let srcYear = year;
    if (srcWeek < 1) { srcYear -= 1; srcWeek = 52; }
    setCopyPreviewLoading(true);
    try {
      const res = await api.post('/planning/copy-week', {
        source_year: srcYear, source_week: srcWeek,
        target_year: year, target_week: week, overwrite, dry_run: true, include_double: includeDouble,
        ...activeCopyFilters,
      });
      if (res.data?.success) {
        setCopyPreview({ ...res.data.data, __overwrite: overwrite });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ön izleme başarısız');
    } finally {
      setCopyPreviewLoading(false);
    }
  };

  // Şub 2026 — Hücre rengini kalıcı olarak değiştirir. null gönderilirse renk kaldırılır.
  const handleSetCellColor = async (assignment, hex) => {
    if (!assignment?.id) return;
    try {
      await api.patch(`/planning/assignments/${assignment.id}/cell-color`, { color: hex });
      // Board'u yeniden çek — cell_color güncel state ile render edilsin.
      await fetchBoard();
      toast.success(hex ? 'Hücre rengi güncellendi' : 'Hücre rengi kaldırıldı');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Renk güncellenemedi');
    }
  };

  // Inline depot rename — triggered from the depot-name label in FirmGroup.
  // Returns a truthy value on success so the child can collapse the editor.
  // Backend cascades the new name to all routes/assignments referencing the
  // depot, so we just call PATCH /planning/depots/{id} and refetch.
  const handleRenameDepot = async (depotId, newName) => {
    if (!depotId || !newName) return false;
    try {
      await api.patch(`/planning/depots/${depotId}`, { name: newName });
      toast.success('Depo adı güncellendi');
      fetchBoard();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncellenemedi');
      return false;
    }
  };

  // Mark a single (route, date) cell as "kapalı"
  // "Tüm günü kapat" — bir gün için board'daki TÜM boş rota hücrelerini
  // status='empty' olarak işaretler. Operatör kilitli / tatil günlerini hızlıca
  // kapatmak için kullanır (öncesinde her rota hücresini tek tek kapatmak
  // zorundaydı). Dolu hücrelere dokunulmaz; yalnızca atanmamış rotalara
  // "Kapalı" placeholder eklenir.
  const handleCloseAllDay = async (dateStr) => {
    const allRoutes = [];
    for (const c of routeGroups || []) {
      for (const d of c.depots || []) {
        for (const r of d.routes || []) allRoutes.push(r);
      }
    }
    const emptyRoutes = allRoutes.filter((r) => !assignByCell.has(`${r.id}|${dateStr}`));
    if (emptyRoutes.length === 0) {
      toast.info('Bu günde kapatılacak boş hücre yok');
      return;
    }
    setConfirmState({
      title: `${dateStr} — ${emptyRoutes.length} rotayı kapat`,
      message: `Bu günün tüm boş hücrelerine "Kapalı" işareti eklenecek. Dolu hücreler etkilenmez. Devam?`,
      confirmLabel: 'Hepsini kapat',
      onConfirm: async () => {
        setConfirmState(null);
        let closed = 0;
        const failed = [];
        for (const r of emptyRoutes) {
          try {
            await api.post('/planning/assignments', {
              year, week_number: week, assignment_date: dateStr,
              company_id: r.company_id, depot_id: r.depot_id, route_id: r.id,
              employee_id: null,
              firm_id: r.owner_firm_id || singleBoardFirmId || undefined,
              status: 'empty',
            });
            closed += 1;
          } catch {
            failed.push(r.route_code);
          }
        }
        fetchBoard();
        if (closed > 0) {
          toast.success(`${closed} rota kapatıldı${failed.length ? `, ${failed.length} atlandı` : ''}`);
        } else {
          toast.error('Hiçbir rota kapatılamadı');
        }
      },
    });
  };

  const handleCloseEmptyCell = async (route, dateStr) => {
    try {
      await api.post('/planning/assignments', {
        year, week_number: week, assignment_date: dateStr,
        company_id: route.company_id,
        depot_id: route.depot_id,
        route_id: route.id,
        employee_id: null,
        firm_id: route.owner_firm_id || singleBoardFirmId || undefined,
        status: 'empty',
      });
      toast.success('Gün kapatıldı');
      fetchBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kapatılamadı');
    }
  };

  // "Tüm günü aç" — `handleCloseAllDay`'in tersi. O güne ait TÜM `status='empty'`
  // (Kapalı) placeholder'larını siler; gerçek atamalar dokunulmaz. Operatör
  // tatil sonrası "geri açalım" senaryosunda günü tek tıkla normalize eder.
  const handleReopenAllDay = async (dateStr) => {
    const closed = (board?.assignments || []).filter(
      (a) => a.assignment_date === dateStr && a.status === 'empty' && !a.employee_id
    );
    if (closed.length === 0) {
      toast.info('Bu günde açılacak kapalı rota yok');
      return;
    }
    setConfirmState({
      title: `${dateStr} — ${closed.length} kapalı rotayı aç`,
      message: `Bu günün tüm "Kapalı" işaretleri silinecek (gerçek atamalar etkilenmez). Devam?`,
      confirmLabel: 'Hepsini aç',
      onConfirm: async () => {
        setConfirmState(null);
        let opened = 0;
        const failed = [];
        for (const a of closed) {
          try {
            await api.delete(`/planning/assignments/${a.id}`);
            opened += 1;
          } catch {
            failed.push(a.route_code);
          }
        }
        fetchBoard();
        if (opened > 0) {
          toast.success(`${opened} rota açıldı${failed.length ? `, ${failed.length} atlandı` : ''}`);
        } else {
          toast.error('Hiçbir rota açılamadı');
        }
      },
    });
  };

  // Temizleme: tüm hafta, aktif filtredeki rotalar veya tek rota (Pzt–Cmt).
  const runClear = async (body) => {
    try {
      const res = await api.post('/planning/week-assignments/clear', { year, week, ...body });
      const n = res.data?.data?.deleted ?? 0;
      const snapshotId = res.data?.data?.snapshot_id;
      if (snapshotId && n > 0) {
        toast.success(`${n} atama silindi`, {
          duration: 15000,
          action: {
            label: 'Geri Al',
            onClick: async () => {
              try {
                const r = await api.post(`/planning/week-restore/${snapshotId}`);
                toast.success(`${r.data?.data?.restored ?? 0} atama geri yüklendi`);
                fetchBoard();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Geri yüklenemedi (snapshot süresi dolmuş olabilir)');
              }
            },
          },
          description: '30 dakika içinde geri alabilirsiniz.',
        });
      } else {
        toast.success(`${n} atama silindi`);
      }
      fetchBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Temizlenemedi');
    } finally {
      setConfirmState(null);
    }
  };

  const handleClearWeek = () => {
    const all = board?.assignments || [];
    const filtered = hasActiveCopyFilters;
    const routeIds = filtered
      ? (routeGroups || []).flatMap((c) => (c.depots || []).flatMap((d) => (d.routes || []).map((r) => r.id)))
      : null;
    const idSet = routeIds ? new Set(routeIds) : null;
    const total = idSet ? all.filter((a) => idSet.has(a.route_id)).length : all.length;
    if (total === 0) {
      toast.info(filtered ? 'Aktif filtredeki rotalarda atama yok' : 'Bu haftada atama yok');
      return;
    }
    setConfirmState({
      title: filtered ? `W${week} (${year}) — filtredeki rotaları temizle` : `W${week} (${year}) haftasını temizle`,
      message: filtered
        ? `Aktif filtre kapsamındaki ${routeIds.length} rotada ${total} atama silinecek.\nFiltre dışındaki rotalar korunur.\n\n30 dakika içinde geri alınabilir. Emin misiniz?`
        : `Bu hafta için ${total} atama silinecek.\n\n30 dakika içinde geri alınabilir. Emin misiniz?`,
      confirmLabel: filtered ? 'Evet, filtredekileri sil' : 'Evet, hepsini sil',
      destructive: true,
      onConfirm: () => runClear(filtered ? { route_ids: routeIds } : {}),
    });
  };

  const handleClearRoute = (route) => {
    const total = (board?.assignments || []).filter((a) => a.route_id === route.id).length;
    if (total === 0) {
      toast.info(`${route.route_code} rotasında bu hafta atama yok`);
      return;
    }
    setConfirmState({
      title: `${route.route_code} — haftayı temizle`,
      message: `${route.route_code} rotasındaki tüm haftanın (Pazartesi–Pazar) ${total} ataması (çalışan ve taşeron) silinecek.\n\n30 dakika içinde geri alınabilir. Emin misiniz?`,
      confirmLabel: 'Evet, rotayı temizle',
      destructive: true,
      onConfirm: () => runClear({ route_ids: [route.id] }),
    });
  };

  // -------- Drag glue --------
  // Two drag sources feed the same drop target:
  //   1. Employee tile (left panel)  → creates a new assignment.
  //   2. Existing AssignmentCell     → moves the assignment to the new cell.
  // We track both via React state (more reliable than dataTransfer JSON across
  // some browsers) and a fallback string id stays in dataTransfer.
  const [draggedAssignment, setDraggedAssignment] = useState(null);
  const [draggedVehicle, setDraggedVehicle] = useState(null);
  const [draggedSubcontractor, setDraggedSubcontractor] = useState(null);
  // Müşteri (company) bazlı accordion — operatör müşteri başlığına basarak
  // o müşterinin tüm depo+rota satırlarını gizleyip sadece müşteri listesini
  // görmek isteyebiliyor (gözü yormasın). Set, kapalı olanları tutar; default
  // olarak hepsi açık.
  const [collapsedCompanies, setCollapsedCompanies] = useState(() => new Set());
  const toggleCompanyCollapsed = useCallback((cid) => {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  }, []);
  // Left panel tab: 'employees' | 'vehicles' | 'subcontractors'
  const [leftTab, setLeftTab] = useState('employees');
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoaded, setVehiclesLoaded] = useState(false);
  // Taşeronlar (manuel şoför+plaka kayıtları) — kullanıcı düz `employees`
  // şemasını bozmadan tek seferlik veya geçici sürücüler ekleyebilsin diye.
  // İleride alt-firma kaydına evrilebilir; şu an `planning_subcontractors`
  // koleksiyonunda tutuluyor.
  const [subcontractors, setSubcontractors] = useState([]);
  const [subcontractorsLoaded, setSubcontractorsLoaded] = useState(false);
  const [subcoCompanies, setSubcoCompanies] = useState([]);
  const [subcDialogOpen, setSubcDialogOpen] = useState(false);
  // Dwell-to-drop: if the operator hovers over a cell for `DWELL_MS`, we
  // auto-create an assignment without requiring a release. Lets you "paint"
  // multiple days in one continuous drag (Pzt → Sal → Çar). Cells already
  // painted in the current drag are tracked so re-hovering doesn't duplicate.
  const DWELL_MS = 700;
  const [dwellingCell, setDwellingCell] = useState(null); // {routeId, dateStr}
  const dwellTimerRef = useRef(null);
  const dwellPaintedRef = useRef(new Set()); // "routeId|dateStr" of already-dropped cells in this drag

  // ─── Tap-to-Pick (mobil/dokunmatik atama) ──────────────────────────────────
  // Drag&drop mobil tarayıcılarda çalışmaz; operatör sol panelden çalışana
  // dokunup ardından boş hücreye dokunarak atama yapabilsin diye eklendi.
  // - selectedEmpId mevcutsa boş hücre tıklaması atama oluşturur.
  // - Aynı çalışana 2. kez dokunmak seçimi kaldırır (Esc gibi).
  // - Dolu hücre tıklaması burada hiçbir şey yapmaz; mevcut sağ-tık/✕ akışları korunur.
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const onTapEmp = useCallback((emp) => {
    if (!canEditPlanning) return;
    setSelectedEmpId((prev) => (prev === emp.id ? null : emp.id));
  }, [canEditPlanning]);
  const onTapEmptyCell = useCallback(async (route, dateStr) => {
    if (!canEditPlanning || !selectedEmpId) return;
    const emp = employees.find((x) => x.id === selectedEmpId);
    if (!emp) { setSelectedEmpId(null); return; }
    const cellKey = `${route.id}|${dateStr}`;
    if (assignByCell.has(cellKey)) return; // dolu hücreyi tap-mode yoksay
    if (dwellPaintedRef.current.has(cellKey)) return;
    dwellPaintedRef.current.add(cellKey);
    const res = await createAssignmentFor(emp, route, dateStr);
    if (!res.ok) dwellPaintedRef.current.delete(cellKey);
    if (!res.ok && res.reason === 'double-booked') {
      const existing = res.existing;
      setConfirmState({
        title: 'Çift atama onayı',
        message: `${emp.full_name} ${dateStr} tarihinde ${existing?.route_code || 'başka bir rotada'} (${existing?.company_name || ''}) zaten atanmış.\nYine de eklensin mi?\n(Aynı gün 2. plan oluşacak.)`,
        confirmLabel: 'Evet, ekle',
        onConfirm: async () => {
          await createAssignmentFor(emp, route, dateStr, { force: true });
          setConfirmState(null);
        },
      });
    }
    // Seçim atama sonrasında korunur — operatör arka arkaya birden çok güne
    // aynı kişiyi atayabilsin (mobilde özellikle hızlı). İptal için tile'a
    // tekrar dokunabilir veya Esc tuşuna basabilir.
  }, [canEditPlanning, selectedEmpId, employees, assignByCell, createAssignmentFor]);

  // Esc ile seçimi temizle (masaüstü kolaylığı).
  useEffect(() => {
    if (!selectedEmpId) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedEmpId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEmpId]);

  const onDragStart = (e, emp) => {
    setDraggedEmployee(emp);
    setDraggedAssignment(null);
    setDraggedVehicle(null);
    dwellPaintedRef.current = new Set();
    try {
      e.dataTransfer.setData('text/plain', emp.id);
      e.dataTransfer.effectAllowed = 'copy';
    } catch { /* dt may be null in some test envs */ }
    // Cleanup once the drag ends — important so dwellPainted state doesn't
    // bleed into the next drag and so dragOver-only dwell paints (no drop
    // event fires) are still released cleanly.
    const onEnd = () => {
      setDraggedEmployee(null);
      cancelDwell();
      e.target.removeEventListener('dragend', onEnd);
    };
    e.target.addEventListener('dragend', onEnd);
  };
  const onAssignmentDragStart = (e, assignment) => {
    setDraggedAssignment(assignment);
    setDraggedEmployee(null);
    setDraggedVehicle(null);
    dwellPaintedRef.current = new Set();
    try {
      e.dataTransfer.setData('text/plain', `assign:${assignment.id}`);
      e.dataTransfer.effectAllowed = 'move';
    } catch { /* dt may be null in some test envs */ }
    // Auto-clear ghost when the drag operation ends, even if the user dropped
    // outside a valid target or pressed Escape.
    const onEnd = () => {
      setDraggedAssignment(null);
      cancelDwell();
      e.target.removeEventListener('dragend', onEnd);
    };
    e.target.addEventListener('dragend', onEnd);
  };

  // Vehicle drag from the "Araçlar" left-panel tab. Drops an existing vehicle
  // onto a cell that already has an assignment → patches `vehicle_code`.
  // Cells without an employee silently reject (we toast the operator).
  const onVehicleDragStart = (e, vehicle) => {
    setDraggedVehicle(vehicle);
    setDraggedEmployee(null);
    setDraggedAssignment(null);
    setDraggedSubcontractor(null);
    dwellPaintedRef.current = new Set();
    try {
      e.dataTransfer.setData('text/plain', `vehicle:${vehicle.id}`);
      e.dataTransfer.effectAllowed = 'copy';
    } catch { /* test envs */ }
    const onEnd = () => {
      setDraggedVehicle(null);
      cancelDwell();
      e.target.removeEventListener('dragend', onEnd);
    };
    e.target.addEventListener('dragend', onEnd);
  };

  // Subcontractor drag — taşeron şoför kartı bir hücreye bırakıldığında
  // backend'e `subcontractor_id` ile POST gönderiyoruz; backend hidrasyon
  // yapıp `employee_name` ve `vehicle_code` alanlarını dolduruyor.
  const onSubcontractorDragStart = (e, sub) => {
    setDraggedSubcontractor(sub);
    setDraggedEmployee(null);
    setDraggedAssignment(null);
    setDraggedVehicle(null);
    dwellPaintedRef.current = new Set();
    try {
      e.dataTransfer.setData('text/plain', `subc:${sub.id}`);
      e.dataTransfer.effectAllowed = 'copy';
    } catch { /* test envs */ }
    const onEnd = () => {
      setDraggedSubcontractor(null);
      cancelDwell();
      e.target.removeEventListener('dragend', onEnd);
    };
    e.target.addEventListener('dragend', onEnd);
  };

  const cancelDwell = () => {
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
    setDwellingCell(null);
  };

  // Dwell entry: arm a timer to auto-drop after DWELL_MS while hovering this
  // cell. Skip if the cell is already filled, employee is on leave that day,
  // we already painted this cell in the current drag, OR there's a paint
  // currently in flight for that cell. The "in flight" guard kapatır şu race
  // koşulunu: kullanıcı bir hücrede dwell tetikledi → backend POST gitti
  // (henüz cevap dönmedi, fetchBoard yenilemedi); kullanıcı kısa süre başka
  // hücreye gidip aynı hücreye döndü → dwellPainted henüz set edilmediği
  // için ikinci bir POST gidiyor → çift kayıt + çakışma. Şimdi timer
  // tetiklenir tetiklenmez key'i `paintedRef`'e ekliyoruz; sadece createFor
  // başarısız dönerse geri çıkarıyoruz.
  const onDragOverCell = (e, route, dateStr) => {
    if (!canEditPlanning) return;
    e.preventDefault(); // allow drop
    // Only employee-tile drags trigger dwell. Cell-to-cell move is one-shot.
    if (!draggedEmployee || draggedAssignment) return;
    const key = `${route.id}|${dateStr}`;
    if (dwellPaintedRef.current.has(key)) return;
    if (assignByCell.has(key)) return;
    if (leaveByEmpDate.has(`${draggedEmployee.id}|${dateStr}`)) return;
    // Same cell → keep the existing timer running.
    if (dwellingCell && dwellingCell.routeId === route.id && dwellingCell.dateStr === dateStr) return;
    cancelDwell();
    setDwellingCell({ routeId: route.id, dateStr });
    const empSnap = draggedEmployee;
    dwellTimerRef.current = setTimeout(async () => {
      // Re-check guards (state may have changed).
      if (assignByCell.has(key)) { cancelDwell(); return; }
      // Mark BEFORE awaiting so a fast back-and-forth on the same cell
      // can't trigger a second POST while this one is still in flight.
      dwellPaintedRef.current.add(key);
      const res = await createAssignmentFor(empSnap, route, dateStr, { silent: false });
      // Failure (validation / locked / server error) → operatör tekrar
      // deneyebilsin diye kaydı geri çıkar.
      if (!res.ok) dwellPaintedRef.current.delete(key);
      // Don't clear draggedEmployee — drag is still ongoing. Just clear the dwell.
      setDwellingCell(null);
      dwellTimerRef.current = null;
    }, DWELL_MS);
  };
  const onDragLeaveCell = (route, dateStr) => {
    if (!dwellingCell) return;
    if (dwellingCell.routeId === route.id && dwellingCell.dateStr === dateStr) {
      cancelDwell();
    }
  };
  const onDropCell = async (e, route, dateStr) => {
    e.preventDefault();
    cancelDwell();
    // Kilitli tarihler artık atamayı bloklamıyor — kullanıcı isteği üzerine
    // (May 03, 2026): tatil/kilit günleri yalnızca görsel bir uyarı; operatör
    // gerekirse yine de atama yapabilsin. Sadece görsel kilit rozeti ve
    // background rengi kalıyor; toast uyarısı da kaldırıldı (operatör
    // farkındaysa zaten görüyor).
    // Branch 0 — dropping a vehicle plate. Updates vehicle_code on existing
    // assignment cell. If the cell is empty we explicitly tell the operator
    // they need an assignment first (vehicle is only meaningful with a
    // worker).
    if (draggedVehicle) {
      const v = draggedVehicle;
      setDraggedVehicle(null);
      const a = assignByCell.get(`${route.id}|${dateStr}`);
      if (!a) {
        toast.error('Önce çalışan atayın, sonra plaka takın');
        return;
      }
      try {
        await api.put(`/planning/assignments/${a.id}`, { vehicle_code: v.license_plate });
        toast.success(`${v.license_plate} → ${a.employee_name || a.route_code}`);
        fetchBoard();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Plaka atanamadı');
      }
      return;
    }
    // Branch 0b — dropping a subcontractor (taşeron) tile. Boş hücre gerekli;
    // dolu hücreye bırakılırsa operatöre uyarı veriyoruz (taşeron atamayı
    // mevcut çalışanın üstüne yazmasın). Hücre dolu olduğunda yine de geçirmek
    // istenirse önce mevcut atamayı silmeli.
    if (draggedSubcontractor) {
      const sub = draggedSubcontractor;
      setDraggedSubcontractor(null);
      const cellKey = `${route.id}|${dateStr}`;
      if (assignByCell.has(cellKey)) {
        toast.error('Hücre dolu — önce mevcut atamayı kaldırın');
        return;
      }
      try {
        await api.post('/planning/assignments', {
          year, week_number: week, assignment_date: dateStr,
          company_id: route.company_id, depot_id: route.depot_id, route_id: route.id,
          subcontractor_id: sub.id,
          status: 'assigned',
        });
        toast.success(`${sub.full_name}${sub.vehicle_code ? ` (${sub.vehicle_code})` : ''} atandı`);
        fetchBoard();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Taşeron atanamadı');
      }
      return;
    }
    // Branch 1 — moving an existing assignment to this cell.
    if (draggedAssignment) {
      const a = draggedAssignment;
      setDraggedAssignment(null);
      // Same cell? do nothing silently.
      if (a.route_id === route.id && a.assignment_date === dateStr) return;
      const res = await moveAssignment(a, route, dateStr);
      if (!res.ok && res.reason === 'double-booked') {
        // Operator wants to allow double-booking → re-confirm.
        setConfirmState({
          title: 'Çift atama onayı',
          message: `${a.employee_name} bu tarihte zaten ${res.existing?.route_code || 'başka bir rotada'} atanmış.\nYine de taşınsın mı?\n(Aynı gün 2. plan oluşacak.)`,
          confirmLabel: 'Evet, taşı',
          onConfirm: async () => {
            await moveAssignment(a, route, dateStr, { force: true });
            setConfirmState(null);
          },
        });
      }
      return;
    }
    // Branch 2 — dropping a fresh employee tile.
    const raw = e.dataTransfer.getData('text/plain') || '';
    const empId = draggedEmployee?.id || (raw.startsWith('assign:') ? null : raw);
    const empSnapshot = draggedEmployee || (empId ? employees.find((x) => x.id === empId) : null);
    setDraggedEmployee(null);
    if (!empSnapshot) return;
    // Çift POST koruması — eğer bu hücre dwell tarafından zaten işlenmişse
    // (timer biraz önce tetiklendi, fetchBoard cevabı henüz dönmedi) operatör
    // hücreye tekrar drop yaparsa ikinci POST gitmesin.
    const cellKey = `${route.id}|${dateStr}`;
    if (dwellPaintedRef.current.has(cellKey)) return;
    dwellPaintedRef.current.add(cellKey);
    const res = await createAssignmentFor(empSnapshot, route, dateStr);
    if (!res.ok) dwellPaintedRef.current.delete(cellKey);
    if (!res.ok && res.reason === 'double-booked') {
      const existing = res.existing;
      setConfirmState({
        title: 'Çift atama onayı',
        message: `${empSnapshot.full_name} ${dateStr} tarihinde ${existing?.route_code || 'başka bir rotada'} (${existing?.company_name || ''}) zaten atanmış.\nYine de eklensin mi?\n(Aynı gün 2. plan oluşacak.)`,
        confirmLabel: 'Evet, ekle',
        onConfirm: async () => {
          await createAssignmentFor(empSnapshot, route, dateStr, { force: true });
          setConfirmState(null);
        },
      });
    }
  };

  // Lazy-load vehicles the first time the operator opens the Araçlar tab.
  // We don't want to bloat the initial board fetch since most planners spend
  // time in the employees view. Subsequent tab switches reuse the cache.
  useEffect(() => {
    if (leftTab !== 'vehicles' || vehiclesLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/vehicles', { params: { limit: 1000 } });
        if (!cancelled && Array.isArray(res.data?.data)) {
          setVehicles(res.data.data);
          setVehiclesLoaded(true);
        }
      } catch {
        if (!cancelled) toast.error('Araçlar yüklenemedi');
      }
    })();
    return () => { cancelled = true; };
  }, [leftTab, vehiclesLoaded]);

  // Subcontractor lazy-load — Taşeron sekmesine ilk geçildiğinde getirilir;
  // yeni eklenenler `setSubcontractors` üzerinden anlık eklenir, full reload
  // gerekmez.
  useEffect(() => {
    if (leftTab !== 'subcontractors' || subcontractorsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const [res, sc] = await Promise.all([
          api.get('/planning/subcontractors'),
          api.get('/subco-companies').catch(() => null),
        ]);
        if (!cancelled) setSubcoCompanies(sc?.data?.data || []);
        if (!cancelled && Array.isArray(res.data?.data)) {
          setSubcontractors(res.data.data);
          setSubcontractorsLoaded(true);
        }
      } catch {
        if (!cancelled) toast.error('Taşeronlar yüklenemedi');
      }
    })();
    return () => { cancelled = true; };
  }, [leftTab, subcontractorsLoaded]);

  const handleDeleteSubcontractor = async (sub) => {
    setConfirmState({
      title: 'Taşeronu pasifleştir',
      message: `${sub.full_name} listeden kaldırılsın mı? Geçmiş atamalar etkilenmez.`,
      confirmLabel: 'Pasifleştir',
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/planning/subcontractors/${sub.id}`);
          setSubcontractors((prev) => prev.filter((s) => s.id !== sub.id));
          toast.success('Taşeron pasifleştirildi');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Silinemedi');
        }
        setConfirmState(null);
      },
    });
  };

  // Filtered subcontractor list — same search box as employees / vehicles.
  const filteredSubcontractors = useMemo(() => {
    const q = normalizeTr((search || '').trim());
    if (!q) return subcontractors;
    return subcontractors.filter((s) =>
      normalizeTr(s.full_name || '').includes(q) ||
      normalizeTr(s.vehicle_code || '').includes(q) ||
      normalizeTr(s.note || '').includes(q)
    );
  }, [subcontractors, search]);

  // Subco firmaları → altında bağlı çalışanlar (Taşeron sekmesi).
  const subcoGroups = useMemo(() => {
    const q = normalizeTr((search || '').trim());
    return subcoCompanies.map((c) => {
      const nameHit = !q || normalizeTr(c.name || '').includes(q);
      return {
        ...c,
        nameHit,
        employees: (employees || []).filter((e) => e.subco_id === c.id && e.role !== 'SUBCO' && (nameHit || normalizeTr(e.full_name || '').includes(q))),
      };
    }).filter((c) => c.nameHit || c.employees.length);
  }, [subcoCompanies, employees, search]);

  // Filtered vehicle list — uses the same search box; respects the left-panel
  // firm filter so the operator sees only trucks belonging to the active
  // employer firm (or all when no filter).
  const filteredVehicles = useMemo(() => {
    const q = normalizeTr(search.trim());
    let list = vehicles;
    if (empFirmIds.size > 0) list = list.filter((v) => empFirmIds.has(v.firm_id));
    if (q) {
      list = list.filter((v) =>
        normalizeTr(v.license_plate || '').includes(q) ||
        normalizeTr(v.brand || '').includes(q) ||
        normalizeTr(v.model || '').includes(q)
      );
    }
    return list;
  }, [vehicles, search, empFirmIds]);

  // -------- Filter / availability presentation --------
  const activeFilterCount =
    (filterFirmIds.size > 0 ? 1 : 0) +
    (empFirmIds.size > 0 ? 1 : 0) +
    (filterCompanyIds.size > 0 ? 1 : 0) +
    (filterDepotIds.size > 0 ? 1 : 0) +
    (filterRouteIds.size > 0 ? 1 : 0) +
    (empFilter !== 'all' ? 1 : 0);
  const clearAllFilters = () => {
    setFilterFirmIds(new Set());
    setEmpFirmIds(new Set());
    setFilterCompanyIds(new Set());
    setFilterDepotIds(new Set());
    setFilterRouteIds(new Set());
    setIncludeBorrowed(false);
    setEmpFilter('all');
  };

  const filteredEmployees = useMemo(() => {
    const q = normalizeTr(search.trim());
    let list = q
      ? employees.filter((e) => normalizeTr(e.full_name || '').includes(q))
      : employees;
    // Madde 1 (revize): Tüm kullanıcılar (FULL_ADMIN, FIRM_ADMIN, EMPLOYEE)
    // çalışan listesinde gösterilir; YALNIZCA platform sahibi olan
    // `admin@hrportal.be` (platform-level super admin) hariç tutulur. Diğer
    // SUPER_ADMIN'ler de planlanabilir aktör olabilir — kullanıcı isteği.
    list = list.filter((e) => (e.email || '').toLowerCase() !== 'admin@hrportal.be');
    // Multi-select left-panel firm filter — when one or more firms are picked
    // we show only employees whose primary firm matches OR who are eligible
    // (borrowed) for any of them.
    if (empFirmIds.size > 0 && !includeBorrowed) {
      list = list.filter((e) => {
        if (empFirmIds.has(e.firm_id)) return true;
        const eligible = e.eligible_firm_ids || [];
        return eligible.some((fid) => empFirmIds.has(fid));
      });
    }
    if (empFilter !== 'all') {
      list = list.filter((e) => {
        const booked = employeeBookedDates.get(e.id);
        const hasAnyBooking = booked && booked.size > 0;
        const hasLeave = board?.leave_blocks?.some((lb) => lb.user_id === e.id);
        if (empFilter === 'available') return !hasAnyBooking && !hasLeave;
        if (empFilter === 'assigned') return hasAnyBooking;
        if (empFilter === 'leave') return hasLeave;
        return true;
      });
    }
    // ── Sort: fully-booked employees drop to the bottom. Pazar (Sunday) is
    // always excluded from the count (no Sunday work by convention).
    // Cumartesi artık varsayılan olarak görünür olduğu için (Mayıs 2026
    // değişikliği), gerçek "dolu" eşiği = haftada 6 gün (Pzt-Cmt). Mon-Fri
    // 5 gün doluysa operatör hâlâ Cumartesi'ye ekleyebilir → yukarıda kalsın.
    const sundayDate = board?.week?.end_date || null;
    const weekdayBookedCount = (e) => {
      const set = employeeBookedDates.get(e.id);
      if (!set) return 0;
      let n = 0;
      for (const dt of set) {
        if (dt !== sundayDate) n += 1;
      }
      return n;
    };
    return [...list].sort((a, b) => {
      const aFull = weekdayBookedCount(a) >= 6 ? 1 : 0;
      const bFull = weekdayBookedCount(b) >= 6 ? 1 : 0;
      if (aFull !== bFull) return aFull - bFull; // fully-booked → bottom
      return (a.full_name || '').localeCompare(b.full_name || '', 'tr');
    });
  }, [employees, search, empFilter, employeeBookedDates, board, empFirmIds, includeBorrowed]);

  // Madde 3 (option a): firma bazlı atama özeti — operatör "AYTU'dan 3 kişi,
  // HYK'dan 4 kişi" dağılımını anında görsün. Her firmaya kaç DISTINCT çalışan
  // bu hafta görev aldığını sayarız (assignment sayısı değil — kişi sayısı).
  // Chip'e tıklayınca açılan popover ise o firmanın çalışanlarını ve her
  // birinin atama sayısını listeler — operatör eksik kalan firmaları tek
  // bakışta görüp doğrudan oradan eksik kişiyi sürükleyebilir.
  // - Pazar atamaları hariç (operatör hafta planlamasında haftaiçi düşünür)
  // - cancelled / empty / null employee_id atamaları hariç
  // - filterFirmIds aktifse: yalnızca seçili firmalar görünür
  // - boş chip'ler de gösterilir (0 kişi → operatör hangi firmanın eksik
  //   olduğunu görmek ister)
  const firmAssignmentCounts = useMemo(() => {
    if (!board) return [];
    const sundayDate = board?.week?.end_date || null;
    // firm_id → Map<employee_id, {id, name, count, days: Set<date>}>
    const detailByFirm = new Map();
    // Taşeron (manuel kayıt) atamaları için ayrı bir bucket. Subcontractor_id'yi
    // employee_id yerine kullanıyoruz; aynı taşeron birden çok güne atandığında
    // tek satırda toplansın. Firma chip'ini AYTU/HYK/FAST-TRANS yanına amber
    // renkle ekleyeceğiz.
    const subcEmpMap = new Map();
    for (const a of (board.assignments || [])) {
      if (a.status === 'cancelled' || a.status === 'empty') continue;
      if (sundayDate && a.assignment_date === sundayDate) continue;
      // Subcontractor branch
      if (a.is_subcontractor) {
        const key = a.subcontractor_id || `subc-${a.employee_name || 'anon'}`;
        const cur = subcEmpMap.get(key);
        if (cur) {
          cur.count += 1;
          cur.days.add(a.assignment_date);
        } else {
          subcEmpMap.set(key, { id: key, name: a.employee_name || '—', count: 1, days: new Set([a.assignment_date]) });
        }
        continue;
      }
      if (!a.employee_id) continue;
      const fid = a.firm_id || '__none__';
      if (!detailByFirm.has(fid)) detailByFirm.set(fid, new Map());
      const empMap = detailByFirm.get(fid);
      const cur = empMap.get(a.employee_id);
      if (cur) {
        cur.count += 1;
        cur.days.add(a.assignment_date);
      } else {
        empMap.set(a.employee_id, { id: a.employee_id, name: a.employee_name || '—', count: 1, days: new Set([a.assignment_date]) });
      }
    }
    const firms = board.firms || [];
    const out = [];
    for (const f of firms) {
      if (filterFirmIds.size > 0 && !filterFirmIds.has(f.id)) continue;
      const empMap = detailByFirm.get(f.id);
      const employees = empMap ? [...empMap.values()].sort((x, y) => (x.name || '').localeCompare(y.name || '', 'tr')) : [];
      out.push({ id: f.id, name: f.name, count: employees.length, employees });
    }
    // Taşeron chip'i — sadece bu hafta en az 1 taşeron atama varsa göster.
    // Filter set boşsa veya filtreye taşeron dahil değilse de gizle (operatör
    // bir firma seçtiyse taşeron sayacı dağılımı bozmaz; ayrı bir akış).
    if (subcEmpMap.size > 0 && filterFirmIds.size === 0) {
      const employees = [...subcEmpMap.values()].sort((x, y) => (x.name || '').localeCompare(y.name || '', 'tr'));
      out.push({ id: '__subcontractors__', name: 'Taşeron', count: employees.length, employees, isSubcontractor: true });
    }
    return out;
  }, [board, filterFirmIds]);
  const [openFirmPopover, setOpenFirmPopover] = useState(null); // firm_id | null

  // -------- Week navigation --------
  const goPrevWeek = () => {
    let w = week - 1; let y = year;
    if (w < 1) { y -= 1; w = 52; }
    setYear(y); setWeek(w);
  };
  const goNextWeek = () => {
    let w = week + 1; let y = year;
    if (w > 53) { y += 1; w = 1; }
    setYear(y); setWeek(w);
  };

  // Reset open dialogs when navigating weeks (avoid stale references).
  useEffect(() => { setQuickAssign(null); }, [year, week]);

  // Mobile-only: left panel (employees/vehicles tabs) takes the full screen
  // when opened. We prefer a stateful toggle over a true Sheet component so
  // drag-and-drop from the panel to the board still works on touch — Sheet
  // would unmount on close and break the DnD source element tracking.
  const [mobileLeftOpen, setMobileLeftOpen] = useState(false);

  // Desktop: sol paneli kullanıcı isteğiyle daraltılabilir/genişletilebilir
  // hale getir. Daraltıldığında board (sağdaki tablo) ekranı genişler;
  // tercih localStorage'a kaydedilir, böylece sayfaya her gelişte aynı
  // görünüm korunur. Mobile'da bu state etkin değildir — orada `mobileLeftOpen`
  // tam-ekran toggle olarak çalışır.
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(() => {
    try { return localStorage.getItem('planning.leftPanelCollapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('planning.leftPanelCollapsed', leftPanelCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [leftPanelCollapsed]);

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-100px)] gap-3" data-testid="planning-page">
      {/* Mobile toggle: floats just below the breadcrumb bar. Hidden on md+. */}
      <div className="md:hidden flex items-center gap-2 px-1">
        <Button size="sm" variant="outline" onClick={() => setMobileLeftOpen((v) => !v)} data-testid="planning-mobile-toggle">
          {mobileLeftOpen ? 'Planlama ekranı' : 'Çalışan / Araç listesi'}
        </Button>
      </div>
      {/* ============================== Left Panel — employees / vehicles / subcontractors ============================== */}
      <PlanningLeftPanel
        leftTab={leftTab}
        setLeftTab={setLeftTab}
        search={search}
        setSearch={setSearch}
        empFirmIds={empFirmIds}
        setEmpFirmIds={setEmpFirmIds}
        includeBorrowed={includeBorrowed}
        setIncludeBorrowed={setIncludeBorrowed}
        board={board}
        filteredEmployees={filteredEmployees}
        filteredVehicles={filteredVehicles}
        vehiclesLoaded={vehiclesLoaded}
        subcontractors={filteredSubcontractors}
        subcoGroups={subcoGroups}
        subcontractorsLoaded={subcontractorsLoaded}
        employeeBookedDates={employeeBookedDates}
        employeeWeekSummary={employeeWeekSummary}
        dates={dates}
        singleBoardFirmId={singleBoardFirmId}
        onDragStart={onDragStart}
        onVehicleDragStart={onVehicleDragStart}
        onSubcontractorDragStart={onSubcontractorDragStart}
        onAddSubcontractor={() => setSubcDialogOpen(true)}
        onDeleteSubcontractor={canEditPlanning ? handleDeleteSubcontractor : null}
        onQuickAssignDay={canEditPlanning ? (date, emp) => setQuickAssign({ emp, date }) : null}
        focusedEmpId={focusedEmpId}
        onToggleFocusEmp={toggleFocusedEmp}
        selectedEmpId={selectedEmpId}
        onTapEmp={onTapEmp}
        mobileLeftOpen={mobileLeftOpen}
        leftPanelCollapsed={leftPanelCollapsed}
        setLeftPanelCollapsed={setLeftPanelCollapsed}
      />

      {/* ============================== Main Board ============================== */}
      <div className={`flex-1 flex flex-col gap-3 min-w-0 ${mobileLeftOpen ? 'hidden md:flex' : 'flex'}`}>
        <PlanningToolbar
          year={year}
          week={week}
          board={board}
          t={t}
          goPrevWeek={goPrevWeek}
          goNextWeek={goNextWeek}
          setYear={setYear}
          setWeek={setWeek}
          onWeekCopyDone={(y, w) => {
            // Hedef hafta şu an açıksa board'u yenile ki yeni atamalar
            // hücrelere yansısın. Farklı hafta açıksa yıllık özet zaten
            // WeekPicker içinde tazeleniyor — burada ek iş yok.
            if (y === year && w === week) fetchBoard();
          }}
          isSuperAdmin={canEditPlanning}
          canCopyWeek={canCopyWeek}
          onCopyFromPrev={() => setShowCopyDialog(true)}
          onOpenDepotManager={() => setDepotManagerOpen(true)}
          onOpenImportExport={() => setImportExportOpen(true)}
          onClearWeek={handleClearWeek}
          showSaturday={showSaturday}
          showSunday={showSunday}
          setShowSunday={setShowSunday}
          filterFirmIds={filterFirmIds}
          setFilterFirmIds={setFilterFirmIds}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          filterCompanyIds={filterCompanyIds}
          setFilterCompanyIds={setFilterCompanyIds}
          filterDepotIds={filterDepotIds}
          setFilterDepotIds={setFilterDepotIds}
          filterRouteIds={filterRouteIds}
          setFilterRouteIds={setFilterRouteIds}
          empFilter={empFilter}
          setEmpFilter={setEmpFilter}
          clearAllFilters={clearAllFilters}
          activeFilterCount={activeFilterCount}
          loading={loading}
          assignedCount={assignedCount}
          sundayAssignedCount={sundayAssignedCount}
          missingCount={missingCount}
          conflictCount={conflictCount}
          onOpenConflicts={() => setConflictsOpen(true)}
          firmAssignmentCounts={firmAssignmentCounts}
          openFirmPopover={openFirmPopover}
          setOpenFirmPopover={setOpenFirmPopover}
          dates={dates}
          isDateLocked={isDateLocked}
          lockedDates={lockedDates}
          focusedEmp={focusedEmpId ? employees.find((e) => e.id === focusedEmpId) || null : null}
          focusedEmpAssignedCount={focusedEmpId ? (employeeBookedDates.get(focusedEmpId)?.size || 0) : 0}
          onClearFocus={() => setFocusedEmpId(null)}
        />

        {/* Active filters breadcrumb — shown whenever ANY filter is active.
            Single-tap to remove a specific filter, or "Tümünü temizle" to
            reset everything. */}
        <ActiveFiltersBreadcrumb
          activeFilterCount={activeFilterCount}
          board={board}
          year={year}
          week={week}
          routeGroups={routeGroups}
          assignByCell={assignByCell}
          visibleDates={visibleDates}
          filterFirmIds={filterFirmIds}
          setFilterFirmIds={setFilterFirmIds}
          empFirmIds={empFirmIds}
          setEmpFirmIds={setEmpFirmIds}
          setIncludeBorrowed={setIncludeBorrowed}
          filterCompanyIds={filterCompanyIds}
          setFilterCompanyIds={setFilterCompanyIds}
          filterDepotIds={filterDepotIds}
          setFilterDepotIds={setFilterDepotIds}
          filterRouteIds={filterRouteIds}
          setFilterRouteIds={setFilterRouteIds}
          empFilter={empFilter}
          setEmpFilter={setEmpFilter}
          clearAllFilters={clearAllFilters}
        />

        {/* Tap-to-Pick (mobil atama) durum bandı — bir çalışan seçildiğinde
            görünür, boş hücreye dokunarak atama yapılabileceğini hatırlatır. */}
        {selectedEmpId && (() => {
          const emp = employees.find((x) => x.id === selectedEmpId);
          if (!emp) return null;
          return (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-700 text-xs"
              data-testid="planning-tap-pick-banner"
            >
              <span className="inline-flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                Seçili: {emp.full_name}
              </span>
              <span className="text-blue-700 dark:text-blue-300">
                — boş hücreye dokunarak ata
              </span>
              <button
                type="button"
                onClick={() => setSelectedEmpId(null)}
                className="ml-auto px-2 py-0.5 rounded text-[11px] font-semibold bg-white dark:bg-slate-800 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                data-testid="planning-tap-pick-clear"
              >
                Seçimi kaldır (Esc)
              </button>
            </div>
          );
        })()}

        {/* Board grid. */}
        <Card className="flex-1 overflow-auto p-0" data-testid="planning-board-table-card">
          <table className="w-full border-collapse" data-testid="planning-board-table">
            <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
              <tr>
                <th
                  className="text-left px-2 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 sticky left-0 bg-slate-100 dark:bg-slate-800 z-20 border-b border-r border-slate-200 dark:border-slate-700 relative"
                  style={{ width: firstColWidth, minWidth: firstColWidth, maxWidth: firstColWidth }}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="truncate">{t('planning.firmDepotRoute')}</span>
                    {/* Hepsini Aç / Hepsini Kapat — operatör tek tıkla tüm
                        müşteri grupları için depo/rota detaylarını gizleyip
                        sadece müşteri başlıklarını görmek isteyebiliyor.
                        Buton state'e göre etiket değiştiriyor. */}
                    {(routeGroups || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (collapsedCompanies.size >= routeGroups.length) {
                            setCollapsedCompanies(new Set());
                          } else {
                            setCollapsedCompanies(new Set(routeGroups.map((c) => c.company_id)));
                          }
                        }}
                        className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        title={collapsedCompanies.size >= routeGroups.length ? 'Tüm müşteri gruplarını aç' : 'Tüm müşteri gruplarını kapat'}
                        data-testid="planning-collapse-all-companies"
                      >
                        {collapsedCompanies.size >= routeGroups.length ? '▶ Aç' : '▼ Kapat'}
                      </button>
                    )}
                  </div>
                  {/* Sürüklenebilir sütun ayırıcı: hover'da maviye döner,
                      mousedown ile resize başlar, mouseup'ta localStorage'a
                      yazılır. 120–360px arasıyla sınırlandırılmıştır. */}
                  <div
                    onMouseDown={onFirstColResizeStart}
                    onDoubleClick={() => { setFirstColWidth(170); try { localStorage.setItem('planning.firstColWidth', '170'); } catch { /* ignore */ } }}
                    title="Sürükleyerek genişliği ayarla (çift tıkla varsayılana dön)"
                    data-testid="planning-first-col-resizer"
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-emerald-400/60 active:bg-emerald-500/80 transition-colors"
                  />
                </th>
                {DAY_NAMES.map((d, i) => {
                  // Madde 3: cumartesi/pazar default gizli; tatil ise toggle
                  // açık olsa bile yine atlıyoruz (banner zaten haber veriyor).
                  if (isDayHidden(i, dates[i])) return null;
                  // Short, locale-aware header: "16 Nis Pzt" / "16 Apr Mon".
                  // Full weekday name stays in the tooltip for accessibility.
                  const tLong = t(`planning.days.${i}`);
                  const longName = (typeof tLong === 'string' && !tLong.startsWith('planning.')) ? tLong : d;
                  const nextLong = i < 6
                    ? (() => { const v = t(`planning.days.${i + 1}`); return (typeof v === 'string' && !v.startsWith('planning.')) ? v : DAY_NAMES[i + 1]; })()
                    : '';
                  return (
                  <th key={d} className={`text-left px-2 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 border-b border-r border-slate-200 dark:border-slate-700 min-w-[150px] ${isDateLocked(dates[i]) ? 'bg-rose-50 dark:bg-rose-900/20' : ''}`}>
                    <div className="flex items-center justify-between gap-1">
                      <div>
                        <div title={isDateLocked(dates[i]) ? `${longName} · ${lockedDates[dates[i]]}` : longName} className="flex items-center gap-1">
                          {columnHeaderLabel(t, dates[i], i)}
                          {isDateLocked(dates[i]) && (
                            <span className="relative group inline-flex">
                              <Lock
                                className="h-3 w-3 text-rose-500 cursor-help"
                                data-testid={`planning-day-locked-${dates[i]}`}
                              />
                              {/* Hover tooltip — kilitli gün gerekçesini chip'ten
                                  ayrı görsel banner harcamadan ihtiyaç anında
                                  gösterir. group-hover ile triggers. */}
                              <span
                                role="tooltip"
                                className="pointer-events-none absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-lg group-hover:opacity-100 transition-opacity"
                                data-testid={`planning-day-locked-tooltip-${dates[i]}`}
                              >
                                {lockedDates[dates[i]]}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      {canEditPlanning && i < 6 && (
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => handleCopyDayToNext(i)}
                            className="opacity-50 hover:opacity-100 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition rounded p-1"
                            title={t('planning.copyDayToNext', { day: longName, next: nextLong })}
                            data-testid={`planning-copy-day-${i}`}
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                          {/* Spread to rest of WEEKDAYS — hide on Cmt because
                              the only remaining day (Pazar) is intentionally
                              excluded from the bulk spread. */}
                          {i < 5 && (
                            <button
                              onClick={() => handleSpreadDay(i)}
                              className="opacity-50 hover:opacity-100 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition rounded p-1"
                              title={t('planning.spreadDayTitle', { day: longName, remaining: String(5 - i) })}
                              data-testid={`planning-spread-day-${i}`}
                            >
                              <ChevronsRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {/* Günün tüm boş rotalarını "Kapalı" olarak işaretle —
                              kilitli / tatil günlerinde operatör hepsini tek
                              tıkla kapatabilsin diye. Onay dialog'u açılır
                              (yanlışlıkla basışları önler). */}
                          <button
                            onClick={() => handleCloseAllDay(dates[i])}
                            className="opacity-50 hover:opacity-100 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition rounded p-1"
                            title={`${longName}: Tüm boş rotaları kapat`}
                            data-testid={`planning-close-all-day-${i}`}
                          >
                            <ChevronsDown className="h-3.5 w-3.5" />
                          </button>
                          {/* Tersi: günün tüm "Kapalı" placeholder'larını sil —
                              operatör tatil sonrası "yanlışlıkla kapatmıştık,
                              hepsini geri aç" akışını tek tıkla yapsın diye.
                              Yalnızca o günde en az 1 'empty' status varsa
                              etkili; yoksa info toast göstererek noop. */}
                          <button
                            onClick={() => handleReopenAllDay(dates[i])}
                            className="opacity-50 hover:opacity-100 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition rounded p-1"
                            title={`${longName}: Tüm kapalı rotaları aç`}
                            data-testid={`planning-reopen-all-day-${i}`}
                          >
                            <ChevronsUp className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {routeGroups.map((c) => (
                <FirmGroup
                  key={c.company_id}
                  company={c}
                  dates={visibleDates}
                  firstColWidth={firstColWidth}
                  lockedDates={lockedDates}
                  assignByCell={assignByCell}
                  onDropCell={onDropCell}
                  onDragOverCell={onDragOverCell}
                  onDragLeaveCell={onDragLeaveCell}
                  dwellingCell={dwellingCell}
                  dwellMs={DWELL_MS}
                  onRemoveAssignment={removeAssignment}
                  onContextMenu={canEditPlanning ? setCtxMenu : null}
                  onAssignmentDragStart={canEditPlanning ? onAssignmentDragStart : null}
                  draggingAssignmentId={draggedAssignment?.id || null}
                  onCloseEmptyCell={canEditPlanning ? handleCloseEmptyCell : null}
                  onRenameDepot={canEditPlanning ? handleRenameDepot : null}
                  onClearRoute={canEditPlanning && canCopyWeek ? handleClearRoute : null}
                  isSuperAdmin={canEditPlanning}
                  focusedEmpId={focusedEmpId}
                  collapsed={collapsedCompanies.has(c.company_id)}
                  onToggleCollapsed={() => toggleCompanyCollapsed(c.company_id)}
                  liveProgressByCode={ritmonitor.byCode}
                  liveProgressTodayISO={todayISO}
                  onLiveChipClick={openRideDrawer}
                  onTapEmptyCell={onTapEmptyCell}
                  pickModeActive={!!selectedEmpId}
                />
              ))}
              {routeGroups.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-sm text-slate-400">
                    Henüz rota tanımlı değil. Backend seed çalıştırılmış mı?
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      {/* ============================== Copy week dialog ============================== */}
      {showCopyDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { setShowCopyDialog(false); setCopyPreview(null); }}>
          <Card className="max-w-2xl w-full p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="planning-copy-dialog">
            <div className="flex items-center gap-2 mb-3">
              <Copy className="h-5 w-5 text-emerald-500" />
              <h3 className="text-lg font-semibold">Önceki Haftadan Kopyala</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              <b>W{week - 1 < 1 ? 52 : week - 1}</b> → <b>W{week}</b> aktarımı. Ön izleme farkları ve çakışmaları listeler; hiçbir değişiklik kaydedilmez.
            </p>

            {/* Şub 2026 — Aktif filtre bildirimi. Operatör bir firma/müşteri/
                depo/rota filtresi uygulamışken kopyalamayı tetiklerse SADECE
                o kapsamdaki atamaların kopyalanacağını açıkça uyar; aksi halde
                "hepsini kopyaladı sandım" tuzağına düşülür. */}
            {hasActiveCopyFilters && (
              <div
                className="mb-3 rounded border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-2.5 text-[11px] text-blue-800 dark:text-blue-200"
                data-testid="planning-copy-active-filters-banner"
              >
                <div className="font-semibold flex items-center gap-1 mb-0.5">
                  <Filter className="h-3 w-3" /> Aktif filtreler dikkate alınacak
                </div>
                <div className="text-[10px] opacity-90 flex flex-wrap gap-1.5">
                  {activeCopyFilters.firm_ids && <span className="px-1.5 py-0.5 rounded bg-white/60 dark:bg-slate-800/60 border border-blue-200">Firma: {activeCopyFilters.firm_ids.length}</span>}
                  {activeCopyFilters.company_ids && <span className="px-1.5 py-0.5 rounded bg-white/60 dark:bg-slate-800/60 border border-blue-200">Müşteri: {activeCopyFilters.company_ids.length}</span>}
                  {activeCopyFilters.depot_ids && <span className="px-1.5 py-0.5 rounded bg-white/60 dark:bg-slate-800/60 border border-blue-200">Depo: {activeCopyFilters.depot_ids.length}</span>}
                  {activeCopyFilters.route_ids && <span className="px-1.5 py-0.5 rounded bg-white/60 dark:bg-slate-800/60 border border-blue-200">Rota: {activeCopyFilters.route_ids.length}</span>}
                </div>
                <div className="text-[10px] mt-1 opacity-80">Sadece bu kapsamdaki atamalar kopyalanır; diğer atamalar dokunulmadan kalır.</div>
              </div>
            )}

            {/* Preview trigger */}
            {!copyPreview && (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => copyPreviewFromPreviousWeek(false)}
                  className="w-full"
                  disabled={copyPreviewLoading}
                  data-testid="planning-copy-preview-keep"
                >
                  {copyPreviewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Ön izle (mevcutları koru)
                </Button>
                <Button
                  onClick={() => copyPreviewFromPreviousWeek(true)}
                  variant="outline"
                  className="w-full border-rose-200 text-rose-700 hover:bg-rose-50"
                  disabled={copyPreviewLoading}
                  data-testid="planning-copy-preview-overwrite"
                >
                  Ön izle (üzerine yaz)
                </Button>
                <Button onClick={() => setShowCopyDialog(false)} variant="ghost" className="w-full">
                  Vazgeç
                </Button>
              </div>
            )}

            {/* Preview results */}
            {copyPreview && (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  <div className="rounded-lg border p-2 bg-emerald-50 border-emerald-200">
                    <div className="text-[10px] text-emerald-600">Kopyalanacak</div>
                    <div className="text-2xl font-bold text-emerald-700" data-testid="copy-pv-copied">{copyPreview.copied}</div>
                  </div>
                  <div className="rounded-lg border p-2 bg-rose-50 border-rose-200">
                    <div className="text-[10px] text-rose-600">Atlanacak</div>
                    <div className="text-2xl font-bold text-rose-700" data-testid="copy-pv-skipped">{copyPreview.skipped}</div>
                  </div>
                  <div className="rounded-lg border p-2 bg-amber-50 border-amber-200">
                    <div className="text-[10px] text-amber-600">Üzerine yaz.</div>
                    <div className="text-2xl font-bold text-amber-700" data-testid="copy-pv-overwrite">{(copyPreview.would_overwrite || []).length}</div>
                  </div>
                  <div className="rounded-lg border p-2 bg-slate-50">
                    <div className="text-[10px] text-slate-500">Hedefte var</div>
                    <div className="text-2xl font-bold text-slate-700">{copyPreview.target_existing}</div>
                  </div>
                </div>

                {copyPreview.locked_hits > 0 && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    {copyPreview.locked_hits} atama tatil/kilitli günlere denk geliyor — yine de kopyalanır.
                  </div>
                )}

                {/* Overwrite list */}
                {(copyPreview.would_overwrite || []).length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-1">Üzerine yazılacak hücreler ({copyPreview.would_overwrite.length})</div>
                    <div className="max-h-32 overflow-y-auto text-xs border rounded p-2 bg-amber-50/50 space-y-0.5">
                      {copyPreview.would_overwrite.slice(0, 50).map((o, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-slate-500">{o.date}</span>
                          <span className="font-semibold">{o.route_code}</span>
                          <span className="text-slate-500 line-through">{o.dst_employee || '—'}</span>
                          <span className="text-slate-400">→</span>
                          <span className="text-amber-700 font-medium">{o.src_employee}</span>
                        </div>
                      ))}
                      {copyPreview.would_overwrite.length > 50 && (
                        <div className="text-[10px] text-slate-400 italic pt-1">+{copyPreview.would_overwrite.length - 50} daha</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Conflicts */}
                <CopyConflictsSection
                  preview={copyPreview}
                  loading={copyPreviewLoading}
                  onToggleIncludeDouble={(v) => copyPreviewFromPreviousWeek(!!copyPreview.__overwrite, v)}
                />

                {/* Confirm actions */}
                <div className="flex flex-col gap-2 pt-3 border-t">
                  <Button
                    onClick={() => { copyFromPreviousWeek(!!copyPreview.__overwrite, activeCopyFilters, !!copyPreview.include_double); setShowCopyDialog(false); setCopyPreview(null); }}
                    className="w-full"
                    variant={copyPreview.__overwrite ? 'destructive' : 'default'}
                    data-testid="planning-copy-confirm-apply"
                  >
                    {copyPreview.__overwrite ? 'Onayla ve üzerine yaz' : 'Onayla ve kopyala'}
                  </Button>
                  <Button onClick={() => setCopyPreview(null)} variant="outline" className="w-full">
                    Geri
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ============================== Quick-assign dialog ============================== */}
      {quickAssign && (
        <QuickAssignDialog
          quickAssign={quickAssign}
          routeGroups={routeGroups}
          assignByCell={assignByCell}
          leaveByEmpDate={leaveByEmpDate}
          weekDates={dates}
          onClose={() => setQuickAssign(null)}
          onPick={async (route, applyAllWeek) => {
            if (applyAllWeek) {
              await createAssignmentForWeek(quickAssign.emp, route, dates);
              setQuickAssign(null);
            } else {
              const res = await createAssignmentFor(quickAssign.emp, route, quickAssign.date);
              if (res.ok) setQuickAssign(null);
            }
          }}
        />
      )}

      {/* ============================== Right-click context menu ============================== */}
      {ctxMenu && (
        <CellContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          assignment={ctxMenu.assignment}
          route={ctxMenu.route}
          weekDates={dates}
          onSpreadEmployee={() => handleSpreadEmployeeFromCell(ctxMenu.assignment)}
          onCopyToNext={() => handleCopyCellToNext(ctxMenu.assignment)}
          onRemove={() => handleRemoveFromCtx(ctxMenu.assignment)}
          onSetColor={(hex) => handleSetCellColor(ctxMenu.assignment, hex)}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {/* ============================== Generic confirm dialog ============================== */}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        destructive={confirmState?.destructive}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />

      {/* ============================== Depot Manager (super admin) ============================== */}
      <DepotManagerDialog
        open={depotManagerOpen}
        board={board}
        scopeFirmId={singleBoardFirmId}
        scopeCompanyId={depotManagerScopeCompany}
        onClose={() => { setDepotManagerOpen(false); setDepotManagerScopeCompany(null); }}
        onChange={() => fetchBoard()}
      />

      {/* ============================== Conflicts panel ============================== */}
      <ConflictsDialog
        open={conflictsOpen}
        conflicts={conflictList}
        onClose={() => setConflictsOpen(false)}
        onRemoveAssignment={(a) => {
          // Confirm before deleting from inside the panel.
          setConfirmState({
            title: 'Atamayı sil',
            message: `${a.employee_name} · ${a.route_code} (${a.assignment_date}) silinecek.`,
            confirmLabel: 'Sil',
            destructive: true,
            onConfirm: () => {
              removeAssignment(a);
              setConfirmState(null);
            },
          });
        }}
        onJumpToCell={(a) => {
          const sel = `[data-testid="planning-cell-${a.route_id}-${a.assignment_date}"]`;
          const el = document.querySelector(sel);
          if (!el) return;
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-1');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-1'), 1500);
        }}
      />

      {/* ============================== Excel Import / Export ============================== */}
      <ImportExportDialog
        open={importExportOpen}
        onClose={() => setImportExportOpen(false)}
        year={year}
        week={week}
        board={board}
        onImported={() => fetchBoard()}
      />

      {/* ============================== Subcontractor (Taşeron) ekleme ============================== */}
      <SubcontractorDialog
        open={subcDialogOpen}
        onClose={() => setSubcDialogOpen(false)}
        onCreated={(sub) => {
          setSubcontractors((prev) => [sub, ...prev]);
          setSubcontractorsLoaded(true);
          setLeftTab('subcontractors');
        }}
      />

      {/* ============================== PostNL Ride Detail Drawer ============================== */}
      {rideDrawer && (
        <RideDetailDrawer
          ritnaam={rideDrawer.ritnaam}
          liveSnapshot={rideDrawer.liveSnapshot}
          onClose={() => setRideDrawer(null)}
        />
      )}
    </div>
  );
}
